import type { LensCustomView } from '~/types/leLens';

export const CUSTOM_VIEW_STARTER: LensCustomView = {
  version: 1,
  height: 760,
  html: `<main class="lens-report">
  <header class="hero">
    <div>
      <p class="eyebrow">LIVE LENS · GOVERNED DATA</p>
      <h1 id="lens-title">Performance overview</h1>
      <p id="lens-description" class="lede">A custom reading surface backed by the normal Lens refresh.</p>
    </div>
    <div class="receipt"><span class="pulse"></span><span id="freshness">Waiting for live data…</span></div>
  </header>
  <section id="metrics" class="metrics" aria-label="Headline metrics"></section>
  <section class="story">
    <div>
      <p class="eyebrow">WHAT MOVED</p>
      <h2>Every number below comes from the saved Lens datasets</h2>
    </div>
    <div id="bars" class="bars"></div>
  </section>
  <footer><span>Custom presentation</span><span>Queries, permissions, filters and refresh stay governed by Lens</span></footer>
</main>`,
  css: `:root { color-scheme: light; font-family: Inter, "Helvetica Neue", Arial, sans-serif; color: #16302b; background: #f6f1e8; }
* { box-sizing: border-box; }
body { margin: 0; background: #f6f1e8; }
.lens-report { min-height: 100%; padding: 48px clamp(24px, 5vw, 72px); }
.hero { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 32px; align-items: end; padding-bottom: 36px; border-bottom: 1px solid rgba(22,48,43,.18); }
.eyebrow { margin: 0 0 12px; color: #28796c; font-size: 12px; font-weight: 750; letter-spacing: .13em; }
h1, h2 { font-family: Georgia, "Times New Roman", serif; font-weight: 500; letter-spacing: -.03em; }
h1 { max-width: 800px; margin: 0; font-size: clamp(44px, 6vw, 76px); line-height: .98; }
h2 { max-width: 620px; margin: 0; font-size: clamp(28px, 3.3vw, 44px); line-height: 1.08; }
.lede { max-width: 700px; margin: 18px 0 0; color: #50635e; font-size: 17px; line-height: 1.55; }
.receipt { display: flex; gap: 9px; align-items: center; padding: 10px 14px; border: 1px solid rgba(40,121,108,.25); border-radius: 999px; background: rgba(255,255,255,.62); color: #38564f; font-size: 12px; white-space: nowrap; }
.pulse { width: 8px; height: 8px; border-radius: 50%; background: #2ca58d; box-shadow: 0 0 0 4px rgba(44,165,141,.13); }
.metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin: 26px 0 52px; }
.metric { min-height: 160px; padding: 22px; border: 1px solid rgba(22,48,43,.12); border-radius: 4px; background: rgba(255,255,255,.82); }
.metric-label { min-height: 36px; margin: 0; color: #5c6d68; font-size: 13px; line-height: 1.35; }
.metric-value { margin: 20px 0 0; color: #143f37; font-family: Georgia, serif; font-size: clamp(28px, 3.2vw, 44px); line-height: 1; }
.metric-note { margin: 10px 0 0; color: #77847f; font-size: 11px; }
.story { display: grid; grid-template-columns: minmax(260px, .8fr) minmax(360px, 1.2fr); gap: clamp(40px, 7vw, 100px); padding: 38px 0 54px; }
.bars { display: grid; align-content: start; gap: 17px; }
.bar-label { display: flex; justify-content: space-between; gap: 18px; margin-bottom: 7px; color: #38564f; font-size: 12px; }
.track { height: 9px; overflow: hidden; border-radius: 999px; background: rgba(22,48,43,.09); }
.fill { height: 100%; border-radius: inherit; background: #e2644b; }
.empty { color: #77847f; font-size: 14px; line-height: 1.5; }
footer { display: flex; justify-content: space-between; gap: 24px; padding-top: 18px; border-top: 1px solid rgba(22,48,43,.18); color: #77847f; font-size: 11px; }
@media (max-width: 850px) { .hero, .story { grid-template-columns: 1fr; } .metrics { grid-template-columns: repeat(2, 1fr); } .receipt { justify-self: start; } }
@media (max-width: 520px) { .lens-report { padding: 28px 18px; } .metrics { grid-template-columns: 1fr; } footer { flex-direction: column; } }`,
  javascript: `Lens.ready(function (payload) {
  document.getElementById('lens-title').textContent = payload.lens.title;
  document.getElementById('lens-description').textContent = payload.lens.description || 'A live custom reading of this Lens.';

  var ready = payload.cards.filter(function (card) { return card.status === 'ready' && card.rows && card.rows.length; });
  var freshest = ready.map(function (card) { return card.refreshedAt; }).filter(Boolean)[0];
  document.getElementById('freshness').textContent = freshest ? 'Live data · refreshed ' + freshest : 'Live data connected';

  var metrics = ready.slice(0, 4).map(function (card) {
    var row = card.rows[0] || {};
    var key = (card.columns || Object.keys(row)).find(function (name) { return typeof row[name] === 'number'; });
    var raw = key ? row[key] : '—';
    var value = typeof raw === 'number' ? new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1, notation: Math.abs(raw) >= 1000000 ? 'compact' : 'standard' }).format(raw) : String(raw);
    return '<article class="metric"><p class="metric-label">' + Lens.escape(card.title) + '</p><p class="metric-value">' + Lens.escape(value) + '</p><p class="metric-note">' + Lens.escape(card.source || 'Lens dataset') + '</p></article>';
  }).join('');
  document.getElementById('metrics').innerHTML = metrics || '<p class="empty">No ready metric rows yet. The view will update when Lens data arrives.</p>';

  var seriesCard = ready.find(function (card) { return card.rows.length > 1; });
  if (!seriesCard) { document.getElementById('bars').innerHTML = '<p class="empty">Add a multi-row card to turn this area into a live comparison.</p>'; return; }
  var columns = seriesCard.columns || Object.keys(seriesCard.rows[0]);
  var valueKey = columns.find(function (key) { return typeof seriesCard.rows[0][key] === 'number'; });
  var labelKey = columns.find(function (key) { return key !== valueKey; });
  var top = seriesCard.rows.slice(0, 7);
  var max = Math.max.apply(null, top.map(function (row) { return Math.abs(Number(row[valueKey])) || 0; })) || 1;
  document.getElementById('bars').innerHTML = top.map(function (row) {
    var value = Number(row[valueKey]) || 0;
    return '<div><div class="bar-label"><span>' + Lens.escape(row[labelKey]) + '</span><strong>' + Lens.escape(new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1, notation: 'compact' }).format(value)) + '</strong></div><div class="track"><div class="fill" style="width:' + Math.max(2, Math.abs(value) / max * 100) + '%"></div></div></div>';
  }).join('');
});`,
};
